/**
 * Ticket category list state machine - replaces useTicketCategoryStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type {
  TicketCategory,
  TicketMainCategory,
  TicketSubCategory,
  SelectedCategory,
} from '../types'

export interface TicketCategoryListContext {
  categories: TicketCategory[]
  selectedCategory: SelectedCategory | null
}

export type TicketCategoryListEvent =
  | { type: 'SET_CATEGORIES'; categories: TicketCategory[] }
  | { type: 'ADD_CATEGORY'; category: TicketCategory }
  | { type: 'UPDATE_CATEGORY'; id: string; updates: Partial<TicketCategory> }
  | { type: 'DELETE_CATEGORY'; id: string }
  | { type: 'SET_SELECTED_CATEGORY'; category: SelectedCategory | null }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as TicketCategoryListContext,
    events: {} as TicketCategoryListEvent,
  },
})

export const ticketCategoryListMachine = machineSetup.createMachine({
  id: 'ticketCategoryList',
  context: {
    categories: [],
    selectedCategory: null,
  },
  on: {
    SET_CATEGORIES: {
      actions: assign({
        categories: ({ event }) => (event.type === 'SET_CATEGORIES' ? event.categories : []),
      }),
    },
    ADD_CATEGORY: {
      actions: assign({
        categories: ({ context, event }) =>
          event.type === 'ADD_CATEGORY'
            ? [...context.categories, event.category]
            : context.categories,
      }),
    },
    UPDATE_CATEGORY: {
      actions: assign({
        categories: ({ context, event }) => {
          if (event.type !== 'UPDATE_CATEGORY') return context.categories
          return context.categories.map((cat) =>
            cat.id === event.id ? { ...cat, ...event.updates } : cat
          )
        },
      }),
    },
    DELETE_CATEGORY: {
      actions: assign({
        categories: ({ context, event }) => {
          if (event.type !== 'DELETE_CATEGORY') return context.categories
          return context.categories.filter((cat) => cat.id !== event.id)
        },
      }),
    },
    SET_SELECTED_CATEGORY: {
      actions: assign({
        selectedCategory: ({ event }) =>
          event.type === 'SET_SELECTED_CATEGORY' ? event.category : null,
      }),
    },
    CLEAR: {
      actions: assign({ categories: [], selectedCategory: null }),
    },
  },
})
